import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios, { API } from "@/lib/api";
import { toast } from "sonner";
import { playDing } from "@/components/pos/OnlineOrderNotifier";

// 🔔 New wholesale sign-ups waiting for approval — polls every 30s, rings on new ones, re-rings every 10 min while open.
const TEXTS = {
  zh: { title: "⏳ 新批发客户待审批", approve: "✔ 批准批发", view: "查看", hide: "收起", open: "位客户待审批", again: "仍有批发客户待审批", approved: "✅ 已批准批发", rejected: "已拒绝" },
  es: { title: "⏳ Cliente mayorista por aprobar", approve: "✔ Aprobar", view: "Ver", hide: "Ocultar", open: "cliente(s) por aprobar", again: "Clientes mayoristas aún por aprobar", approved: "✅ Mayorista aprobado", rejected: "Rechazado" },
  en: { title: "⏳ Wholesale sign-up pending", approve: "✔ Approve", view: "View", hide: "Hide", open: "customer(s) pending", again: "Wholesale sign-ups still pending", approved: "✅ Approved", rejected: "Rejected" },
  pt: { title: "⏳ Cliente atacado por aprovar", approve: "✔ Aprovar", view: "Ver", hide: "Ocultar", open: "cliente(s) por aprovar", again: "Clientes ainda por aprovar", approved: "✅ Aprovado", rejected: "Rejeitado" },
};
const cfg = () => TEXTS[localStorage.getItem("app_lang") || "zh"] || TEXTS.zh;

export default function WholesalePendingNotifier() {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [collapsed, setCollapsed] = useState(true);
  const knownRef = useRef(new Set());
  const seededRef = useRef(false);
  const lastRingRef = useRef(0);

  useEffect(() => {
    let cancelled = false;
    const tick = async () => {
      try {
        const r = await axios.get(`${API}/customers/pending-wholesale`);
        if (cancelled) return;
        const list = r.data?.items || [];
        setItems(list);
        const t = cfg();
        const now = Date.now();
        const fresh = list.filter((c) => !knownRef.current.has(c.id));
        fresh.forEach((c) => knownRef.current.add(c.id));
        if (fresh.length > 0 && seededRef.current) {
          setCollapsed(false);
          playDing("loud");
          lastRingRef.current = now;
          fresh.forEach((c) => toast.warning(`${t.title}: ${c.store_name || c.name}`, { description: `${c.phone || ""} ${c.rif || ""}`, duration: 15000 }));
        } else if (list.length > 0 && now - lastRingRef.current >= 600000) {
          playDing();
          lastRingRef.current = now;
          toast.warning(`${t.again} (${list.length})`, { duration: 6000 });
        }
        seededRef.current = true;
      } catch { /* retry next tick */ }
    };
    tick();
    const id = setInterval(tick, 30000);
    return () => { cancelled = true; clearInterval(id); };
  }, []);

  const decide = async (c, approve) => {
    try {
      await axios.post(`${API}/customers/${c.id}/wholesale-decision`, { approve });
      setItems((prev) => prev.filter((x) => x.id !== c.id));
      toast.success(approve ? cfg().approved : cfg().rejected);
    } catch (e) { toast.error(e?.response?.data?.detail || "Error"); }
  };

  if (items.length === 0) return null;
  const t = cfg();
  if (collapsed) {
    return (
      <button onClick={() => setCollapsed(false)} data-testid="ws-pending-collapsed"
        className="fixed bottom-36 right-4 z-[890] bg-amber-500 text-black font-black px-4 py-2.5 rounded-full shadow-2xl animate-pulse text-sm">
        ⏳ {items.length} {t.open}
      </button>
    );
  }
  return (
    <div className="fixed bottom-36 right-4 z-[890] w-[min(92vw,360px)] bg-amber-600 border-4 border-amber-200 rounded-2xl shadow-2xl text-black overflow-hidden" data-testid="ws-pending-panel">
      <div className="flex items-center justify-between px-4 py-2 bg-amber-700 text-white">
        <span className="font-black text-base">{t.title}</span>
        <button onClick={() => setCollapsed(true)} className="text-xs underline opacity-90" data-testid="ws-pending-hide">{t.hide}</button>
      </div>
      <div className="max-h-[45vh] overflow-y-auto divide-y divide-amber-400/60">
        {items.map((c) => (
          <div key={c.id} className="px-4 py-3 space-y-1.5" data-testid={`ws-pending-${c.id}`}>
            <div className="font-black text-base leading-tight">{c.store_name || c.name}</div>
            <div className="text-xs font-semibold opacity-90">{c.name !== c.store_name ? `${c.name} · ` : ""}{c.phone || "-"}{c.rif ? ` · ${c.rif}` : ""}{c.address ? ` · ${c.address}` : ""}</div>
            <div className="flex gap-2">
              <button onClick={() => decide(c, true)} className="flex-1 bg-emerald-600 text-white font-black py-1.5 rounded-lg hover:bg-emerald-500 text-sm" data-testid={`ws-pending-approve-${c.id}`}>{t.approve}</button>
              <button onClick={() => { setCollapsed(true); navigate("/admin/customers?filter=pending"); }} className="px-3 bg-white text-amber-800 font-bold py-1.5 rounded-lg text-sm" data-testid={`ws-pending-view-${c.id}`}>{t.view}</button>
              <button onClick={() => decide(c, false)} className="px-3 bg-amber-800 text-white font-bold py-1.5 rounded-lg text-sm" data-testid={`ws-pending-reject-${c.id}`}>✕</button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
